/** @file Validates a sector table: shape, equal lengths, increasing thresholds, contiguous drawn tiles. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { SectorTable } from '@zilo/pose3d-format/types/facet';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import { SECTOR_TABLE_SHAPE } from '@zilo/pose3d-format/validate/shapes/sectorTableShape';

const fail = (path: string, rule: string): never => throwError(ERROR_CODES.SECTOR_TABLE_SHAPE, { rule }, { path });

/** Returns `value` as a `SectorTable`; throws `VALIDATION` (shape) or `SECTOR_TABLE_SHAPE` with a path. */
export function validateSectorTable(value: unknown, path: string): SectorTable {
  const table = expectShape(value, path, SECTOR_TABLE_SHAPE) as SectorTable;
  const n = table.thresholds.length;
  for (const key of ['tile', 'flip', 'sideBack'] as const) {
    if (table[key].length !== n) fail(`${path}/${key}`, `${key} has one entry per threshold (${n})`);
  }
  if (table.thresholds.some((t, i) => i > 0 && t <= (table.thresholds[i - 1] ?? 0))) {
    fail(`${path}/thresholds`, 'thresholds strictly increase');
  }
  const drawn = new Set(table.tile.filter((t) => t >= 0));
  if (drawn.size === 0 || [...drawn].some((t) => t >= drawn.size)) {
    fail(`${path}/tile`, 'drawn tiles are numbered 0..count-1 without gaps');
  }
  return table;
}
